import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";

/**
 * A reusable modal for getting a reason before declining a transaction.
 * 
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Function to close the modal.
 * @param {function} onSubmit - Function called with the reason string on submit.
 * @param {string} title - The title of the modal.
 * @param {boolean} isLoading - Whether the submission is in progress.
 */
const DeclineReasonModal = ({ isOpen, onClose, onSubmit, title = "Decline Reason", isLoading = false, data }) => {
  const [reason, setReason] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (reason.trim()) {
      onSubmit(reason);
      setReason("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] w-full p-4 overflow-hidden">
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transition-all duration-200 transform animate-in fade-in zoom-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 transition-colors hover:text-gray-600"
        >
          <X size={20} />
        </button>

        {/* Modal content */}
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="mb-2 font-sans text-xl font-bold text-gray-900">
              {title}
            </h3>

            <p className="font-sans text-sm text-gray-500">
              Please provide a reason for declining this request. This will be sent as feedback.
            </p>

            <div className="p-1 mt-3 bg-gray-100 rounded-lg">
              <p className="font-sans text-sm text-gray-500">
                Reference: <span className="font-semibold">{data?.reference}</span>
              </p>

              <p className="font-sans text-sm text-gray-500">
                Gateway Ref: <span className="font-semibold">{data?.paymentGatewayReference || ""}</span>
              </p>

              <div className="flex gap-3 justify-center items-center">
                <p className="font-sans text-sm text-gray-500">
                  Name: <span className="font-semibold">{data?.user?.firstName} {data?.user?.lastName}</span>
                </p>
                <p className="font-sans text-sm text-gray-500">
                  Amount: <span className="font-semibold">{data?.currency?.symbol || data?.wallet?.currency?.symbol}{data?.amount?.toLocaleString()}</span>
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col">
              <label className="block mb-2 font-sans text-sm font-semibold text-gray-700">
                Feedback/Reason
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter decline reason here..."
                required
                rows={4}
                className="p-3 w-full font-sans text-base rounded-xl border border-gray-200 transition-all outline-none resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 font-sans font-bold text-gray-700 rounded-xl border border-gray-200 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !reason.trim()}
                className="flex-1 py-3 px-4 bg-red-600 text-white rounded-xl font-bold font-sans hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Declining...
                  </>
                ) : (
                  "Confirm Decline"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeclineReasonModal;
